import { setCookie } from "@/lib/setCookie";
import { getRequiredFields, LOGIN_ROLES } from "@/lib/utils";
import { setLoggedInUserData } from "@/redux/slices";
import { routes } from "@/routes/utils";
import { useLoginMutation, useProfileMutation } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { EMAIL, PASSWORD } from "./consts";
import { schema } from "./validations";

const useLogin = () => {
  const navigate = useNavigate();
  const [isPassword, setIsPassword] = useState<boolean>(true);
  const dispatch = useDispatch();

  const form = useForm<any>({
    defaultValues: {
      [EMAIL]: "",
      [PASSWORD]: "",
    },
    resolver: zodResolver(schema),
  });

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(schema);

  // Login mutation
  const { mutate: loginMutate, isPending: isLoginPending } = useLoginMutation({
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.issues?.message
          || error?.response?.data?.message
          || "Something went wrong during login",
        {
          className: "!bg-red-500 !text-white",
        }
      );
    },
    onSuccess: async (data) => {
      const { access_token } = data?.data?.data || {};
      if (access_token) {
        // Store access token
        setCookie("accessToken", access_token);
        // Fetch profile after successful login
        profileMutate();
      } else {
        toast.error("Login failed: No access token received", {
          className: "!bg-red-500 !text-white",
        });
      }
    },
  });

  // Profile mutation
  const { mutate: profileMutate, isPending: isProfilePending } = useProfileMutation({
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.issues?.message || error?.response?.data?.message ||  "Failed to fetch profile"
          ,{
          className: "!bg-red-500 !text-white",
        }
      );
      setCookie("accessToken", ""); // Clear token on profile fetch failure
      navigate(`/${routes.login}`);
    },
    onSuccess: (data) => {
      const profileData = data?.data?.data;
      if (profileData?.role) {
        // Store profile data with role in Redux
        dispatch(setLoggedInUserData({
          ...profileData,
          roles: [profileData.role], // Store role in the same format as expected
        }));

        // Navigate based on role
        if (profileData.role.includes(LOGIN_ROLES.SUPER_ADMIN)) {
          navigate(`/${routes.admin}`);
        } else if (profileData.role.includes(LOGIN_ROLES.SALES_OPS)) {
          navigate(`/${routes.salesOps}`);
        } else if(profileData.role.includes(LOGIN_ROLES.CRM_HEAD)){
          navigate(`/${routes.crm_head}`);
        } else if(profileData.role.includes(LOGIN_ROLES.FINANCE_TEAM)){
          navigate(`/${routes.finance_team}`);
        } else if(profileData.role.includes(LOGIN_ROLES.LABS_TEAM_HEAD)){
          navigate(`/${routes.lab_team_head}`);      
        } else if(profileData.role.includes(LOGIN_ROLES.LABS_TEAM)){
          navigate(`/${routes.lab_team}`);      
        } else if(profileData.role.includes(LOGIN_ROLES.INVENTORY_TEAM)){
          navigate(`/${routes.inventory_team}`);      
        } else if(profileData.role.includes(LOGIN_ROLES.REGION_HEAD)){
          navigate(`/${routes.region_head}`);      
        } else if(profileData.role.includes(LOGIN_ROLES.RELATIONSHIP_MANAGER)){
          navigate(`/${routes.relationship_manager}`);      
        } else {
          toast.error(
            "No valid roles assigned. Please contact the system admin.",
            {
              className: "!bg-red-500 !text-white",
            }
          );
          setCookie("accessToken", "");
          navigate(`/${routes.login}`);
        }
      } else {
        toast.error(
          "No roles assigned to this user. Please contact the system admin.",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        setCookie("accessToken", "");
        navigate(`/${routes.login}`);
      }
    },
  });

  const onSubmit = (data: any) => {
    loginMutate({ data });
  };

  const onEyeIconClick = () => {
    setIsPassword(!isPassword);
  };

  const Icon = isPassword ? Eye : EyeOff;

  const onForgotClick = () => {
    navigate(`/${routes.forgotPassword}`);
  };

  return {
    navigate,
    form,
    requiredFields,
    onSubmit,
    onEyeIconClick,
    isPassword,
    Icon,
    isLoginPending,
    isProfilePending,
    onForgotClick,
  };
};

export default useLogin;